import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useGetPostBySlugQuery } from "@/features/api/postApi";
import { Helmet } from "react-helmet-async";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import parse from "html-react-parser";

const PostView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetPostBySlugQuery(slug);

  useEffect(() => {
    if (error) {
      toast.error("Failed to load post");
    }
  }, [error]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !data?.post) {
    return (
      <div className="text-center py-10">
        <h2 className="text-xl text-red-500">❌ Post not found</h2>
        <button
          onClick={() => navigate("/admin/posts")}
          className="mt-4 text-blue-600 hover:underline"
        >
          Back to Posts
        </button>
      </div>
    );
  }

  const post = data.post;
  const formattedDate = post?.createdAt
    ? format(new Date(post.createdAt), "MMMM dd, yyyy")
    : "";

  const featuredImageUrl =
    typeof post.featuredImage === "string"
      ? post.featuredImage
      : post.featuredImage?.url || "";

  const featuredImageAlt =
    typeof post.featuredImage === "object"
      ? post.featuredImage?.altText || "Featured image"
      : "Featured image";

  const sanitizedHtml = DOMPurify.sanitize(post.content || "", {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: [
      "allow",
      "allowfullscreen",
      "frameborder",
      "scrolling",
      "src",
      "width",
      "height",
    ],
  });

  return (
    <div className="min-h-screen flex flex-col">
      {/* SEO */}
      <Helmet>
        <title>{post.title}</title>
        <meta name="description" content={post.metaDescription || post.title} />
      </Helmet>

      {/* Main Content */}
      <main className="flex-grow px-5 sm:px-6 md:px-8 lg:px-10 xl:px-20 max-w-screen-xl mx-auto mt-5 pb-10">
        {/* Breadcrumb */}
        <div className="mb-4 text-sm text-gray-500">
          <Link to="/" className="hover:underline">Home</Link>
          <span className="mx-2">/</span>
          <span>{post.title}</span>
        </div>

        {/* Post Title */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">{post.title}</h1>

        {/* Date & Status */}
        {formattedDate && (
          <div className="text-sm sm:text-base text-gray-500 mb-6">
            <span>{formattedDate}</span>
            {post.status && (
              <span
                className={`ml-2 font-semibold ${
                  post.status === "published" ? "text-green-600" : "text-yellow-600"
                }`}
              >
                {post.status === "published" ? "" : "Draft"}
              </span>
            )}
          </div>
        )}

        {/* Featured Image */}
        {featuredImageUrl && (
          <div className="mb-8 flex justify-center">
            <img
              src={featuredImageUrl}
              alt={featuredImageAlt}
              className="w-[65%] h-auto rounded-lg shadow-xl object-cover"
            />
          </div>
        )}

        {/* Post Content */}
        <div className="prose max-w-none">
          {sanitizedHtml.trim()
            ? parse(sanitizedHtml)
            : <p>This post has no content yet.</p>}
        </div>
      </main>
    </div>
  );
};

export default PostView;
